/**
 * **A sheet of paper** (Rebuild 32's `Print.dc.html`, the design-debt order — Print after
 * Marketing): every piece of paper CIDER prints is one family — the apple and a masthead ruled
 * under it (the title, its line, a stamp at the right), rows of a name, its line and a figure
 * (with a box to tick where the paper is filled in by hand), a big line where the paper is
 * mostly one thing (a badge's name, a voucher's code), and a foot. The sizes are the drawing's,
 * in points, so the page prints at the size it was drawn.
 */
import type { ReactNode } from 'react';

export interface PaperRow {
  a: ReactNode;
  b?: ReactNode | undefined;
  /** The figure or the line to write on, at the right, in mono. */
  c?: ReactNode | undefined;
  /** A box to tick by hand. */
  box?: boolean | undefined;
  key?: string | undefined;
}
export interface PaperProps {
  /** letter · badge · check · slip — the drawing's four sheets. */
  size?: 'letter' | 'badge' | 'check' | 'slip';
  masthead: ReactNode;
  submast?: ReactNode | undefined;
  stamp?: ReactNode | undefined;
  rows?: readonly PaperRow[];
  big?: ReactNode | undefined;
  bigSub?: ReactNode | undefined;
  /** A QR or a barcode under the big line. */
  code?: ReactNode | undefined;
  foot?: ReactNode | undefined;
  /** What a paper carries before its rows — a letter's paragraphs, a waiver's text. */
  children?: ReactNode;
}

export function Paper({
  size = 'letter',
  masthead,
  submast,
  stamp,
  rows = [],
  big,
  bigSub,
  code,
  foot,
  children,
}: PaperProps): ReactNode {
  return (
    <div className={`ui-paper ui-paper--${size}`}>
      <div className="ui-paper__mast">
        <img className="ui-paper__logo" src="/paper-logo.png" alt="Apple Fest" />
        <span className="ui-paper__titles">
          <h1 className="ui-paper__title">{masthead}</h1>
          {submast ? <span className="ui-paper__sub">{submast}</span> : null}
        </span>
        {stamp ? <span className="ui-paper__mono-stamp">{stamp}</span> : null}
      </div>
      {children}
      {rows.map((r, i) => (
        <div key={r.key ?? String(i)} className="ui-paper__row">
          {r.box ? <span className="ui-paper__box" aria-hidden="true" /> : null}
          <span className="ui-paper__ab">
            <span className="ui-paper__a">{r.a}</span>
            {r.b ? <span className="ui-paper__b">{r.b}</span> : null}
          </span>
          {r.c ? <span className="ui-paper__mono-figure">{r.c}</span> : null}
        </div>
      ))}
      {big ? (
        <div className="ui-paper__bigwrap">
          <div className="ui-paper__big">{big}</div>
          {bigSub ? <div className="ui-paper__bigsub">{bigSub}</div> : null}
          {code ? <div className="ui-paper__code">{code}</div> : null}
        </div>
      ) : null}
      {foot ? <div className="ui-paper__foot">{foot}</div> : null}
    </div>
  );
}
